/**
 * @file: MediaComposeRow.tsx
 * @copyright: (c) 2019-2026 sichuan zhichetech co., ltd.
 *
 * The editable counterpart of {@link MediaAttachmentGrid}: thumbnails of the
 * in-progress uploader items (progress overlay while uploading, an error
 * state, a per-tile remove button) followed by a trailing "+" add tile.
 */

import { Typography } from '@euler/app/components/typography';
import { LeadMediaUploadItem } from '@euler/app/flows/leads/functions/useLeadMediaUploader';
import { LD } from '@euler/app/flows/leads/pages/detail/components/leadDetailKit';
import { Img } from '@euler/components/image/AdvancedImage';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { Play, Plus, X } from 'lucide-react-native';
import { ActivityIndicator, TouchableOpacity, View } from 'react-native';

export const MediaComposeRow = ({
  items,
  onAdd,
  onRemove,
}: {
  items: LeadMediaUploadItem[];
  onAdd: () => void;
  onRemove: (key: string) => void;
}) => {
  const { rpx } = useResponsiveContext();
  const tile = rpx(72);
  const tileStyle = {
    width: tile,
    height: tile,
    borderRadius: rpx(10),
    overflow: 'hidden' as const,
    backgroundColor: LD.hairline,
  };
  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: rpx(8) }}>
      {items.map(item => {
        // Kind is corrected from the server result once uploaded (the picker
        // can mislabel a video as an image).
        const isVideo =
          item.kind === 'video' || (item.mimeType ?? '').startsWith('video/');
        // Prefer the hosted cover once uploaded (a video file uri can't render
        // as an image); while a video is still uploading, show a dark
        // placeholder. An image previews from its on-device uri meanwhile.
        const coverUri = isVideo
          ? item.result?.coverUrl ?? null
          : item.result?.coverUrl ?? item.localUri;
        return (
          <View key={item.key} style={tileStyle}>
            {coverUri ? (
              <Img
                uri={coverUri}
                resizeMode="cover"
                style={{ width: tile, height: tile }}
              />
            ) : (
              <View
                style={{ width: tile, height: tile, backgroundColor: LD.ink }}
              />
            )}
            {isVideo ? (
              <View
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <View
                  style={{
                    width: rpx(26),
                    height: rpx(26),
                    borderRadius: rpx(13),
                    backgroundColor: 'rgba(0,0,0,0.45)',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Play color="#fff" size={rpx(13)} fill="#fff" />
                </View>
              </View>
            ) : null}

            {item.status === 'uploading' ? (
              <View
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0,0,0,0.4)',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ActivityIndicator color="#fff" size="small" />
                <Typography.Text size={rpx(11)} color="#fff" mt={rpx(2)}>
                  {Math.round(item.progress * 100)}%
                </Typography.Text>
              </View>
            ) : null}

            {item.status === 'error' ? (
              <View
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(192,54,44,0.55)',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography.Text size={rpx(11)} color="#fff" medium>
                  上传失败
                </Typography.Text>
              </View>
            ) : null}

            <TouchableOpacity
              activeOpacity={0.8}
              hitSlop={{ top: 6, right: 6, bottom: 6, left: 6 }}
              onPress={() => onRemove(item.key)}
              style={{
                position: 'absolute',
                top: rpx(3),
                right: rpx(3),
                width: rpx(18),
                height: rpx(18),
                borderRadius: rpx(9),
                backgroundColor: 'rgba(0,0,0,0.55)',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <X color="#fff" size={rpx(11)} />
            </TouchableOpacity>
          </View>
        );
      })}

      <TouchableOpacity
        activeOpacity={0.7}
        onPress={onAdd}
        style={{
          width: tile,
          height: tile,
          borderRadius: rpx(10),
          borderWidth: 1,
          borderStyle: 'dashed',
          borderColor: LD.mist,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Plus color={LD.mist} size={rpx(24)} />
      </TouchableOpacity>
    </View>
  );
};
